import { FunctionComponent, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useShellLayout } from './useShellLayout';
import Sidebar from './Sidebar';
import HangarHeader from './HangarHeader';

type AppShellProps = {
    children: ReactNode,
}

/**
 * The app shell: a full-height sidebar beside a content column.
 *
 * Replaces the old header + fixed drawer pair. The sidebar is a real flex
 * sibling whose `width` animates, so the content reflows on its own - the
 * previous shell animated `transform` on the drawer over 300ms and
 * `margin-left` on the content over 400ms, two timelines that visibly desynced,
 * with the 144px offset restated as a literal in both places.
 */
export const AppShell: FunctionComponent<AppShellProps> = ({ children }) => {
    const { pathname } = useLocation();
    const { collapsed, animating, chromeless, toggle } = useShellLayout(pathname);

    return (
        <div className="ds-shell-row">
            {!chromeless && (
                <Sidebar
                    pathname={pathname}
                    collapsed={collapsed}
                    animating={animating}
                    onToggle={toggle}
                />
            )}

            <div className="ds-content-col">
                {pathname.startsWith('/hangar-overview') && !chromeless && <HangarHeader />}
                <main className="ds-main scrollbar-thin" id="main">
                    {/* Keep this wrapper: pages rely on `flex-1` at their own root,
                     * which needs a full-height flex column to resolve against. */}
                    <div className="flex min-h-full w-full flex-col">{children}</div>
                </main>
            </div>
        </div>
    );
};

export default AppShell;
